import type { ReactNode } from "react";
import { logoBackdropClasses } from "./Internal/logoBackdrop";

/**
 * The dark-mode pad on its own, for a mark that can't go through
 * `<Image logo>`, plus the inset that carrier artwork needs.
 *
 * Reach for `<Image logo>` first — it applies the pad itself and handles a
 * missing file. This exists for the call sites whose sizing can't be expressed
 * in pixels: the quote row sizes its logo box with container queries
 * (`in-[.quote-card-narrow]:w-[90px]`), so the rows keep a raw `<img>` in a
 * fixed-size box and wrap that in this instead.
 *
 * The `dark:p-1.5` is the difference between the two. Carrier artwork agrees on
 * nothing: some marks carry generous transparent margins, others run right to
 * the edge of their own box, and flush against the pad the tight ones read as
 * cramped.
 *
 * That inset is currently scoped to the call sites that wrap a raw `<img>` in
 * this — the dashboard's quote and shipment rows. Hubble's carrier marks go
 * through `<Image logo>` (see `CarrierLogoImage`), which applies the pad flush,
 * so the same PNG is inset on the dashboard and flush in Hubble. That is a
 * known gap, not a claim that Hubble's marks are a different kind of artwork.
 * Card and bank marks, which do fill their own box and read as heavily bordered
 * once inset, are the reason the flush version has to stay available.
 *
 * Most carrier PNGs have an opaque white box drawn into them, which shows as a
 * white block on the grey pad. `dark:[&_img]:mix-blend-multiply` lets that
 * white take the pad's colour, so white-box and transparent marks sit in the
 * same pad.
 *
 * Light mode renders the mark untouched, pad, inset and blend alike.
 */
export function LogoBackdrop({ children }: { children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center ${logoBackdropClasses} dark:p-1.5 dark:[&_img]:mix-blend-multiply`}
    >
      {children}
    </span>
  );
}
